import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, firstValueFrom, of } from 'rxjs';

import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';

// Enums
import { ActorTypeDescriptor } from '@app/enums/actor-type';

// Models
import { ActorModel } from '@app/models/actor.model';

// Services
import { ToastrService } from 'ngx-toastr';
import { LoggingService } from '@app/services/logging/logging.service';
import { ActorService } from '@app/services/actor/actor.service';

// Utils
import { mapEnumDescriptorToMultiselectOptions, MenuItemInterface } from '@app/utils/input-fields/input-fields.utils';

import { MatRadioModule } from '@angular/material/radio';
import { MatDividerModule } from '@angular/material/divider';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { BaseModalComponent } from '../../../../../components/base-modal/base-modal.component';
import { IncidentStatus } from '@app/enums/incident-status';
import { Motivation, MotivationDescriptor } from '@app/enums/motivation';
import { ConfirmationModalComponent } from '../../../../../components/confirmation-modal/confirmation-modal.component';
import { ActorEditModalData, ActorEditModalResult } from '@app/pages/incidents/details-page/models/actor-edit.model';

export type EditMode = 'searchForActor' | 'createAnActor';

@Component({
  standalone: true,
  selector: 'app-threat-actor-edit',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTooltipModule,
    MatRadioModule,
    FormsModule,
    MatDividerModule,
    MatTableModule,
    MatPaginatorModule,
    BaseModalComponent,
  ],
  templateUrl: './threat-actor-edit-modal.component.html',
  styleUrl: './threat-actor-edit-modal.component.scss',
})
export class ThreatActorEditModalComponent implements OnInit, AfterViewInit {
  protected readonly IncidentStatus = IncidentStatus;
  protected editMode: EditMode = 'searchForActor';
  protected isSavingData = false;
  protected isLoadingMotivationSuggestion = false;

  // Search for Actor
  protected searchForActorForm: FormGroup;
  protected searchForActorFormControl: FormControl;
  protected foundActorsColumns: (keyof ActorModel | 'selectRadio')[] = [
    'selectRadio',
    'name',
    'type',
    'website',
    'description',
  ];
  protected dataSource = new MatTableDataSource<ActorModel>();
  @ViewChild(MatPaginator) private paginator!: MatPaginator;
  protected selectedRow?: ActorModel;
  protected isLoadingSearchResults = false;

  // Create Actor
  protected createActorForm: FormGroup;
  protected actorTypeOptions: MenuItemInterface[] = mapEnumDescriptorToMultiselectOptions(ActorTypeDescriptor);

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly dialogRef: MatDialogRef<ThreatActorEditModalComponent, ActorEditModalResult>,
    private readonly actorService: ActorService,
    private readonly toastrService: ToastrService,
    private readonly loggingService: LoggingService,
    @Inject(MAT_DIALOG_DATA) public actorData: ActorEditModalData,
  ) {
    this.searchForActorForm = this.formBuilder.group({
      searchForActor: ['', Validators.required],
    });
    this.searchForActorFormControl = this.searchForActorForm.get('searchForActor') as FormControl;

    this.createActorForm = this.formBuilder.group({
      id: [null],
      name: ['', Validators.required],
      description: [''],
      website: [''],
      type: [''],
    });
  }

  public ngOnInit(): void {
    this.updateFormGroup(this.actorData?.actor ?? ({} as ActorModel));
  }

  public ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  public onEditModeSelected(selectedEditMode: EditMode): void {
    if (selectedEditMode === 'createAnActor') {
      this.createActorForm.setValue({
        id: null,
        name: '',
        description: '',
        website: '',
        type: '',
      });
    } else if (selectedEditMode === 'searchForActor' && this.selectedRow) {
      this.updateFormGroup(this.selectedRow!);
    }

    this.createActorForm.markAsDirty();
  }

  public onRowSelected(actor: ActorModel): void {
    this.selectedRow = actor;
    this.updateFormGroup(actor);
    this.createActorForm.markAsDirty();
  }

  public async onSearchAsync(): Promise<void> {
    if (!this.searchForActorFormControl.value || typeof this.searchForActorFormControl.value !== 'string') {
      return;
    }

    this.isLoadingSearchResults = true;
    const actorsResult = await firstValueFrom(
      this.actorService.search(this.searchForActorFormControl.value).pipe(
        catchError((error) => {
          const { status } = error;

          if (status === 404) {
            this.dataSource.data = [];
          }
          this.isLoadingSearchResults = false;
          return of([] as ActorModel[]);
        }),
      ),
    );
    this.isLoadingSearchResults = false;

    if (!actorsResult.length) {
      this.dataSource.data = [];
      return;
    }

    this.dataSource.data = actorsResult;
  }

  public async onSaveAsync(): Promise<void> {
    if (this.createActorForm.invalid) {
      return;
    }

    const actor = this.createActorForm.getRawValue() as ActorModel;
    const selectedOrCreatedActor = await this.getSelectedOrCreatedActorAsync(actor);

    if (!selectedOrCreatedActor) {
      return;
    }

    const suggestedMotivation = await this.tryToFindMotivationByThreatActorAsync(selectedOrCreatedActor.name);
    this.dialogRef.close({ actor: selectedOrCreatedActor, motivation: suggestedMotivation });
  }

  public onCancel(): void {
    if (this.isSavingData || this.isLoadingMotivationSuggestion) {
      return;
    }

    this.dialogRef.close();
  }

  private async getSelectedOrCreatedActorAsync(actor: ActorModel): Promise<ActorModel | null> {
    if (actor.id != null) {
      return actor;
    }

    // Validate if name already exists
    const nameExists = await firstValueFrom(this.actorService.nameExists(actor.name));
    if (nameExists) {
      this.createActorForm.controls['name'].setErrors({ nameExists: true }, { emitEvent: true });
      return null;
    }

    this.isSavingData = true;
    this.createActorForm.disable();

    const response = await firstValueFrom(
      this.actorService.editOrCreate(actor).pipe(
        catchError((error: HttpErrorResponse) => {
          const { status, message } = error;

          this.toastrService.error(`Failed to save actor`, `Error`, {
            positionClass: 'toast-bottom-right',
          });

          this.loggingService.logException(new Error(`Failed to save actor, status ${status} - ${message}`));

          return of(null);
        }),
      ),
    );

    this.isSavingData = false;
    this.createActorForm.enable();

    if (!response) {
      return null;
    }

    return response;
  }

  private async tryToFindMotivationByThreatActorAsync(actorName: string): Promise<Motivation | null> {
    if (this.actorData.currentMotivation !== Motivation.Unknown || !actorName) {
      return null;
    }

    this.isLoadingMotivationSuggestion = true;
    const foundMotivation = await firstValueFrom(
      this.actorService.getMotivationByThreatActor(actorName).pipe(
        catchError((error: Error) => {
          this.loggingService.logException(error);
          return of(Motivation.Unknown);
        }),
      ),
    );
    this.isLoadingMotivationSuggestion = false;

    if (foundMotivation === Motivation.Unknown) {
      return null;
    }

    const confirmationResult = await this.openConfirmationDialogAsync(
      'Motivation update',
      `The motivation for the selected actor "${actorName}" was found as "${MotivationDescriptor[foundMotivation]}". Do you want to update the incident motivation to this value?`,
    );

    return confirmationResult ? foundMotivation : null;
  }

  private async openConfirmationDialogAsync(title: string, content: string): Promise<boolean | undefined> {
    const dialogRef = this.dialog.open<ConfirmationModalComponent, { title: string; content: string }, boolean>(
      ConfirmationModalComponent,
      {
        width: '20vw',
        disableClose: true,
        data: {
          title,
          content,
        },
      },
    );

    return await firstValueFrom(dialogRef.afterClosed());
  }

  private updateFormGroup(actor: ActorModel): void {
    Object.keys(actor).forEach((key: string) => {
      const control = this.createActorForm.get(key);

      if (control) {
        const value = (actor as Record<string, unknown>)[key];
        control.setValue(value);
      }
    });
  }
}
